import React from 'react';
import { BarChart, Trash2, Layers, Clock } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных отчетов о ликвидности
 */
const ComparisonSidebar = ({ 
  savedReports, 
  deleteSavedReport, 
  loadSavedReport,
  industryTypes
}) => {
  // Если нет сохраненных отчетов
  if (!savedReports || savedReports.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение отчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные отчеты о ликвидности для сравнения. Выполните расчет и сохраните отчет, чтобы иметь возможность вернуться к нему позже.
        </p>
      </div>
    );
  }

  // Найти название отрасли по ID
  const getIndustryName = (industryId) => {
    const industry = industryTypes.find(i => i.id === industryId);
    return industry ? industry.name : 'Не указана';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение отчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedReports.length} {savedReports.length === 1 ? 'отчет' : savedReports.length < 5 ? 'отчета' : 'отчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedReports.slice().reverse().map((report) => (
          <div 
            key={report.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {report.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedReport(report.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedReport(report.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center mb-1">
                <Clock className="w-3.5 h-3.5 mr-1 text-gray-500" />
                <span className="text-xs">
                  {new Date(report.date).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Отрасль:</div>
                <div className="text-xs text-right font-medium text-gray-700 dark:text-gray-300">
                  {getIndustryName(report.industry)}
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400">Текущая ликвидность:</div>
                <div className={`text-xs text-right font-medium ${
                  report.results?.currentRatio >= 2 ? 'text-green-600 dark:text-green-400' :
                  report.results?.currentRatio >= 1 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {formatNumber(report.results?.currentRatio || 0, 2)}
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400">Быстрая ликвидность:</div>
                <div className={`text-xs text-right font-medium ${
                  report.results?.quickRatio >= 1 ? 'text-green-600 dark:text-green-400' :
                  report.results?.quickRatio >= 0.5 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {formatNumber(report.results?.quickRatio || 0, 2)}
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400">Абсолютная ликвидность:</div>
                <div className={`text-xs text-right font-medium ${
                  report.results?.cashRatio >= 0.2 ? 'text-green-600 dark:text-green-400' :
                  report.results?.cashRatio >= 0.1 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {formatNumber(report.results?.cashRatio || 0, 2)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Информационная подсказка */}
      {savedReports.length > 1 && (
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 text-xs text-indigo-700 dark:text-indigo-300">
          <p>
            Сохранено несколько отчетов. Чтобы увидеть сравнительный анализ в динамике, нажмите кнопку "Показать динамику" внизу раздела результатов.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;